// GearTile is a map tile that shows a gear that turns robots.

import GearTileActionResult from 'app/src/engine/action-results/GearTileActionResult';
import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import {
	GearTileDirection,
	GearTileFloorOrientation,
	GearTileFloorType,
} from 'app/src/engine/tiles/GearTile';
import scheduleAnimation from 'app/src/utils/scheduleAnimation';
import useValueChanged from 'app/src/utils/useValueChanged';
import * as React from 'react';

function getClassNameForFloorType(floorType: GearTileFloorType): string {
	switch (floorType) {
		case GearTileFloorType.Type1:
			return 'GearTile-floor--type1';
		case GearTileFloorType.Type2:
			return 'GearTile-floor--type2';
		case GearTileFloorType.Type3:
			return 'GearTile-floor--type3';
		case GearTileFloorType.Type4:
			return 'GearTile-floor--type4';
	}
}

function getClassNameForFloorOrientation(floorOrientation: GearTileFloorOrientation): string {
	switch (floorOrientation) {
		case GearTileFloorOrientation.Up:
			return 'GearTile-floor--up';
		case GearTileFloorOrientation.Down:
			return 'GearTile-floor--down';
		case GearTileFloorOrientation.Left:
			return 'GearTile-floor--left';
		case GearTileFloorOrientation.Right:
			return 'GearTile-floor--right';
	}
}

type Props = {
	row: number;
	col: number;
	direction: GearTileDirection;
	floorType: GearTileFloorType;
	floorOrientation: GearTileFloorOrientation;
	actionResults?: TileActionResult[] | null;
	completeAnimation?: () => void;
};

export default function GearTile({
	row,
	col,
	direction,
	floorType,
	floorOrientation,
	actionResults,
	completeAnimation,
}: Props): React.ReactElement | null {
	const [turns, setTurns] = React.useState(0);

	// When we're running animations, we need to be able to cancel.
	const cancelAnimationSequenceRef = React.useRef<(() => void) | null>(null);

	// If this component is unmounted (this happens when the user resets), kill
	// any ongoing animations.
	React.useEffect(
		() => (): void => {
			if (cancelAnimationSequenceRef.current) {
				cancelAnimationSequenceRef.current();
			}
		},
		[],
	);

	// Handle animation changes.
	const actionResult =
		actionResults && actionResults.find((result) => result instanceof GearTileActionResult);
	const actionResultChanged = useValueChanged(actionResult);

	React.useEffect(() => {
		if (!actionResult || !actionResultChanged || !completeAnimation) {
			return;
		}

		setTurns((t) => t + 1);

		cancelAnimationSequenceRef.current = scheduleAnimation([
			[
				(): void => {
					completeAnimation();
					cancelAnimationSequenceRef.current = null;
				},
				500,
			],
		]);
	}, [row, col, actionResults, completeAnimation, actionResult, actionResultChanged]);

	const floorClassNames = [
		'GearTile-floor',
		getClassNameForFloorType(floorType),
		getClassNameForFloorOrientation(floorOrientation),
	];
	const bigGearClassNames = [
		'GearTile-bigGear',
		direction === GearTileDirection.Clockwise
			? 'GearTile-bigGear--clockwise'
			: 'GearTile-bigGear--counterClockwise',
	];

	const turnDirection = direction === GearTileDirection.Clockwise ? 1 : -1;

	return (
		<div className="GearTile">
			<div className={floorClassNames.join(' ')} />
			<div className="GearTile-rivets" />
			<div
				className="GearTile-smallGear"
				style={{ transform: `rotate(${turnDirection * -turns * 360}deg)` }}
			/>
			<div
				className={bigGearClassNames.join(' ')}
				style={{ transform: `rotate(${turnDirection * turns * 90}deg)` }}
			/>
		</div>
	);
}
